// NotificationService.ts

import EventNotification from "../../interfaces/eventNotification/EventNotification";

// Function to fetch all notifications of an employee
export async function getNotifications(
  authToken: string,
  page: number = 1,
  pageSize: number = 10
): Promise<EventNotification[] | null> {
  try {
    let apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/notifications?page=${page}&pageSize=${pageSize}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response);
      return null;
    }

    const notifications: EventNotification[] = await response.json();

    return notifications;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return null;
  }
}


export async function getLatestNotifications(
  authToken: string,
  pollingScope: string = "default",
): Promise<EventNotification[] | null> {
  try {
    const storageKey = `lastPollDate:${pollingScope}`;
    const lastPollDate = localStorage.getItem(storageKey);

    // Establish a per-user baseline instead of replaying all notification
    // history when this browser polls for the first time.
    if (!lastPollDate) {
      localStorage.setItem(storageKey, new Date().toISOString());
      return [];
    }
    
    // Create the API URL with the 'since' query parameter
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/notifications/since?since=${encodeURIComponent(lastPollDate)}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response);
      return null;
    }

    const result = await response.json();

    // Assuming the API returns the server's date in the headers or body
    const serverDate = response.headers.get('Date') || new Date().toISOString();
    
    // Store the server's date in localStorage for the next poll
    localStorage.setItem(storageKey, serverDate);

    const notifications: EventNotification[] = result;
    return notifications;
  } catch (error) {
    console.error("Error fetching latest notifications:", error);
    return null;
  }
}

export async function markNotificationRead(
  authToken: string,
  messageId: string
): Promise<boolean> {
  try {
    let apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/notifications/${messageId}`;

    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      // Handle non-OK responses, e.g., 404 for not found
      return false;
    } else { 
        return true;
    }

  } catch (error) {
    // Handle network errors or other exceptions
    console.error("Error fetching participants:", error);
    return false;
  }
}

export async function markAllNotificationsRead(
    authToken: string,
  ): Promise<boolean> {
    try {
      let apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/notifications/mark-all-read`;
  
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // Attempt to read the response body for more context
        const errorBody = await response.text();
        console.error(
          `Failed to mark all notifications as read. Status: ${response.status}`,
          errorBody || "No additional error message from server."
        );
        return false;
      } else { 
          return true;
      }
  
    } catch (error) {
      // Handle network errors or other exceptions
      console.error("Error during markAllNotificationsRead:", error);
      return false;
    }
  }

export async function updateNotificationPreference(
  token: string,
  userId: string,
  emailPreference: boolean,
  inAppPreference: boolean,
  reminderTime: number
): Promise<boolean> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/notifications/addUpdateNotificationPreference`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        emailPreference,
        inAppPreference,
        reminderTime,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    return false;
  }
}

export async function getNotificationPreferenceById(
  token: string,
  userId: string,
): Promise<{ email: boolean; web: boolean, reminderTime: number } | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/notifications/getNotificationPreferenceById/${userId}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch notification preferences");
      return null;
    }

    const result = await response.json();

    if (result?.success && result?.data) {
      return {
        email: result.data.emailPreference,
        web: result.data.inAppPreference,
        reminderTime: result.data.reminderTime
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching notification preferences:", error);
    return null;
  }
}
